import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import RealLogo from "@/components/Logo";
import { scrollToHash, scrollToTop } from "@/lib/scrollTo";
import { useLanguage } from "@/lib/language";

const EASE = [0.16, 1, 0.3, 1];

export const Navigation = () => {
  const { lang, setLang, langs, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (window.__lenis) open ? window.__lenis.stop() : window.__lenis.start();
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        data-testid="header-navigation"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-charcoal/85 backdrop-blur-md border-b border-gold/20 py-2.5"
            : "bg-transparent py-4"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between">
          <a
            href="#top"
            data-testid="nav-logo-link"
            onClick={(e) => {
              e.preventDefault();
              scrollToTop();
            }}
            className="flex items-center transition-transform duration-500 hover:scale-[1.03]"
          >
            <RealLogo className="h-12 lg:h-14 w-auto" />
          </a>
          <nav className="hidden xl:flex items-center gap-6">
            {t.nav.links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.href.slice(1)}`}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToHash(l.href);
                }}
                className="relative text-[11px] uppercase tracking-[0.18em] text-cream/80 hover:text-gold transition-colors duration-300 after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-gold after:transition-all after:duration-500 hover:after:w-full"
              >
                {l.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-4">
            <div
              className="flex items-center gap-1 text-[11px] uppercase tracking-[0.18em]"
              data-testid="language-toggle"
            >
              {langs.map((l, i) => (
                <span key={l} className="flex items-center">
                  {i > 0 && <span className="text-cream/40 mx-1.5">|</span>}
                  <button
                    onClick={() => setLang(l)}
                    data-testid={`lang-option-${l}`}
                    className={`transition-colors duration-300 ${
                      lang === l
                        ? "text-gold after:block"
                        : "text-cream/70 hover:text-cream"
                    }`}
                  >
                    {l.toUpperCase()}
                  </button>
                </span>
              ))}
            </div>
            <button
              data-testid="nav-menu-toggle"
              onClick={() => setOpen(!open)}
              aria-label={open ? t.nav.closeMenu : t.nav.openMenu}
              className="lg:hidden text-cream p-2 -mr-2"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="fixed inset-0 z-40 bg-charcoal flex flex-col justify-center px-8"
            data-testid="nav-mobile-menu"
          >
            <nav className="flex flex-col gap-1">
              {t.nav.links.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  initial={{ opacity: 0, y: 24, filter: "blur(6px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  transition={{ delay: 0.06 * i + 0.1, duration: 0.7, ease: EASE }}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToHash(l.href);
                    setOpen(false);
                  }}
                  className="font-serif text-2xl sm:text-3xl text-cream py-2 border-b border-cream/10 flex items-baseline gap-4"
                >
                  <span className="text-xs text-gold font-sans tracking-widest">
                    0{i + 1}
                  </span>
                  {l.label}
                </motion.a>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
